import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Button,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import TableItem from "../../../components/TableItem";
import { makeStyles } from "@mui/styles";
import {
  getSaleBillPaymentsById,
  updateRemainingAmount,
  updateSaleBillPayments,
} from "../../../services/Sales";
import { getWarehouseTransferBillById } from "../../../services/WarehouseTransfer";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory } from "react-router";
import PaymentOptions from "../../../components/PaymentOptions";
import FormAlert from "../../../components/FormAlert";
import InputField from "../../../FormComponents/InputField";
import dayjs from "dayjs";

const useStyles = makeStyles({
  container: {
    padding: "20px",
    margin: "20px",
    backgroundColor: "#f9f9f9",
  },
  sectionHeader: {
    marginBottom: "10px",
  },
  fieldContainer: {
    marginBottom: "15px",
  },
  tableContainer: {
    marginTop: "20px",
  },
  label: {
    fontWeight: 700,
  },
  value: {
    fontWeight: 400,
  },
});

function WarehouseTransferBillUpdatePayments() {
  const classes = useStyles();
  const history = useHistory();
  const { billId } = useParams();
  const [bill, setBill] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [isLoading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [paymentMethods, setPaymentMethods] = React.useState(null);
  const [displayAlert, setAlert] = React.useState(false);
  const [alertData, setAlertData] = React.useState({ type: "", message: "" });

  useEffect(() => {
    handleRemainingAmounts();
  }, [paymentMethods]);

  const handleRemainingAmounts = () => {
    const paidTotal = paymentMethods?.reduce(
      (sum, payment) => sum + parseFloat(payment.amount),
      0
    );
    const remaining = parseFloat(bill?.totalPrice) - paidTotal;
    setBill((prev) => (prev ? { ...prev, remainingAmount: remaining } : prev));
  };

  const fetchData = async () => {
    setLoading(true);

    const billResult = await getWarehouseTransferBillById(billId);
    const billPaymentResult = await getSaleBillPaymentsById(billId);

    if (billResult && billResult !== "Something went wrong...") {
      setBill(billResult);
      const itemRows = (billResult.transferItems || []).map((item) => ({
        productName: item.productName,
        barcode: item.barcode,
        sellingQty: item.billQty,
        unitPrice: item.pricePerItem,
        totalPrice: item.price,
        totalProfit: item.itemProfit,
        profitMargin: item.profitMargin,
        action: item.productId,
      }));
      setRows(itemRows);
    } else {
      setErrors({ ...errors, _billDataError: "Unable to fetch bill data" });
    }

    if (billPaymentResult !== "Something went wrong...") {
      const billPayments = (billPaymentResult || []).map((p) => ({
        type: p.paymentType,
        amount: p.amount,
        bank: p.bank,
        chequeNumber: p.chequeNumber,
        returnDate: p.returnDate,
        receiptNumber: p.receiptNumber,
        shopName: p.shopName,
        returnDateInput: p.returnDate != null ? dayjs(p.returnDate) : null,
        returnBillNumber: p.returnBillNumber,
      }));
      setPaymentMethods(billPayments);
    } else {
      setErrors({ ...errors, _billPaymentError: "Unable to fetch payment data" });
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    { id: "productName", label: "Product Name", minWidth: 100, editable: false },
    { id: "barcode", label: "Barcode", minWidth: 100, editable: false },
    { id: "sellingQty", label: "Qty", minWidth: 80, editable: false, type: "number", isDecimal: false },
    { id: "unitPrice", label: "Unit Price", minWidth: 100, editable: false, type: "number", isDecimal: false },
    { id: "totalPrice", label: "Total Price", minWidth: 100, editable: false, type: "number", isDecimal: false },
    { id: "totalProfit", label: "Profit", minWidth: 100, editable: false, type: "number", isDecimal: false },
    { id: "profitMargin", label: "Margin", minWidth: 100, editable: false, type: "number", isDecimal: false },
  ];

  const submitValue = async () => {
    if (paymentMethods && paymentMethods.length > 0) {
      const submitBillPayment = await updateSaleBillPayments(billId, paymentMethods);
      const updateBillRemainingAmount = await updateRemainingAmount(bill);

      if (submitBillPayment && updateBillRemainingAmount !== "Something went wrong...") {
        history.push("/template/retail_bill_view/" + billId);
      } else {
        setAlertData({ type: "error", message: "Something went wrong..." });
        setAlert(true);
      }
    }
  };

  return (
    <Paper className={classes.container} elevation={8}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
          <CircularProgress />
        </Box>
      ) : bill ? (
        <>
          <Grid>
            <Tooltip title="Close">
              <IconButton onClick={() => history.goBack()} aria-label="close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>

          <Typography variant="h5" className={classes.sectionHeader} align="center">
            Warehouse Transfer Bill — Payments
          </Typography>
          <Divider />

          <Grid container spacing={3} className={classes.fieldContainer}>
            <Grid item xs={6}>
              <Typography className={classes.label}>Bill No:</Typography>
              <Typography className={classes.value}>{bill.billNumber}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.label}>Bill Date:</Typography>
              <Typography className={classes.value}>
                {bill.transferDate ? new Date(bill.transferDate).toLocaleDateString() : "-"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.label}>Added By:</Typography>
              <Typography className={classes.value}>{bill.addedBy}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.label}>Status:</Typography>
              <Typography className={classes.value}>{bill.status}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.label}>Discount:</Typography>
              <Typography className={classes.value}>
                {bill.discountPercentage > 0
                  ? `${bill.discountPercentage}%`
                  : `Rs: ${bill.discountAmount}`}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.label}>Total Profit:</Typography>
              <Typography className={classes.value}>{bill.totalProfit}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.label}>Total Amount:</Typography>
              <Typography className={classes.value}>{bill.totalPrice}</Typography>
            </Grid>
            <Grid item>
              <InputField
                isdisabled={true}
                name="_remainingAmount"
                value={bill.remainingAmount}
                type="text"
                label="Remaining Amount"
              />
            </Grid>
            {bill.note && (
              <Grid item xs={12}>
                <Typography className={classes.label}>Note:</Typography>
                <Typography className={classes.value}>{bill.note}</Typography>
              </Grid>
            )}
          </Grid>

          <Divider />

          <Typography variant="h6" className={classes.sectionHeader}>
            Transfer Items
          </Typography>

          <Grid container className={classes.tableContainer}>
            <TableItem
              columns={columns}
              rows={rows}
              page={0}
              setPage={() => {}}
              tablePagin={false}
              totalPages={1}
              getData={fetchData}
              showActions={{}}
              actionRoles={{}}
              handleAction={() => {}}
            />
          </Grid>

          <Grid>
            <PaymentOptions
              paymentMethods={paymentMethods}
              setPaymentMethods={setPaymentMethods}
              billType={"RETAIL"}
              paymentTypes={[
                { value: "CASH", label: "Cash" },
                { value: "CHEQUE", label: "Cheque" },
                { value: "BILL", label: "Recover Import Bill" },
                { value: "RECEIPT", label: "Receipt" },
              ]}
            />
          </Grid>

          <Grid style={{ marginTop: "20px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                {displayAlert && (
                  <FormAlert type={alertData.type} message={alertData.message} />
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button fullWidth variant="contained" onClick={submitValue}>
                  Submit
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </>
      ) : (
        <Box sx={{ padding: "20px", textAlign: "center" }}>
          <Typography>No data available</Typography>
        </Box>
      )}
    </Paper>
  );
}

WarehouseTransferBillUpdatePayments.propTypes = {
  billId: PropTypes.any,
};

export default WarehouseTransferBillUpdatePayments;
